import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/*
|-----------------------------------------------------------------------------
| Shared initial-feed visual gate
|-----------------------------------------------------------------------------
|
| A feed may already contain a few items while the rest of its initial batch is
| still loading. Showing the real list at that point causes a one-card flash.
|
| There is another important transition to protect: when Home becomes active
| again after leaving Focus Mode, Virtuoso has to rebuild its viewport. A
| readiness value from the previous Home visit must not hide the skeleton
| before the rebuilt list has painted.
|
| This hook therefore keeps the skeleton visible until:
|
| 1. the complete initial data batch is available;
| 2. Virtuoso reports that the first visible cards are rendered; and
| 3. the browser receives two animation frames to commit the prepared list.
|
| Normal Load More does not disable this hook and does not change the feed key,
| so pagination never brings the full skeleton back.
|
*/

const MINIMUM_RENDERED_CARD_COUNT = 4;

const createReadinessState = (feedKey) => ({
  feedKey,
  isRenderedRangeReady: false,
  isVisualReady: false,
});

/*
|-----------------------------------------------------------------------------
| Reveal only after a stable browser paint
|-----------------------------------------------------------------------------
|
| The first frame lets React and Virtuoso commit the measured cards behind the
| opaque skeleton. The second frame removes the skeleton only after that hidden
| content has had a full rendering opportunity.
|
*/
const revealAfterStablePaint = (callback) => {
  if (typeof window === "undefined") {
    callback();
    return () => {};
  }

  let secondFrameId = null;

  const firstFrameId = window.requestAnimationFrame(() => {
    secondFrameId = window.requestAnimationFrame(callback);
  });

  return () => {
    window.cancelAnimationFrame(firstFrameId);

    if (secondFrameId !== null) {
      window.cancelAnimationFrame(secondFrameId);
    }
  };
};

const useInitialFeedVisualReady = ({
  feedKey,
  items = [],
  enabled = true,
  isInitialDataReady = false,
  scrollParent,
}) => {
  const activeFeedKeyRef = useRef(feedKey);
  activeFeedKeyRef.current = feedKey;

  const previousGateStateRef = useRef({
    feedKey,
    enabled: false,
    isInitialDataReady: false,
  });

  const [readiness, setReadiness] = useState(() =>
    createReadinessState(feedKey)
  );

  const currentReadiness =
    readiness.feedKey === feedKey
      ? readiness
      : createReadinessState(feedKey);

  const requiredRenderedEndIndex = useMemo(() => {
    if (items.length === 0) return -1;

    const requiredCount = Math.min(
      items.length,
      MINIMUM_RENDERED_CARD_COUNT
    );

    return requiredCount - 1;
  }, [items.length]);

  const updateCurrentFeedReadiness = useCallback(
    (patch) => {
      setReadiness((previous) => {
        if (activeFeedKeyRef.current !== feedKey) {
          return previous;
        }

        const base =
          previous.feedKey === feedKey
            ? previous
            : createReadinessState(feedKey);

        return {
          ...base,
          ...patch,
          feedKey,
        };
      });
    },
    [feedKey]
  );

  /*
  |---------------------------------------------------------------------------
  | Reset before paint when a feed becomes visible again
  |---------------------------------------------------------------------------
  |
  | Do not reuse visual readiness from an earlier Home/Focus visit. The list
  | may have been unmounted while another mode was active and must prove that
  | its new Virtuoso viewport is ready before the skeleton disappears.
  |
  */
  useLayoutEffect(() => {
    const previous = previousGateStateRef.current;

    const feedChanged = previous.feedKey !== feedKey;
    const becameEnabled = enabled && !previous.enabled;
    const initialDataBecameReady =
      isInitialDataReady && !previous.isInitialDataReady;
    const gateBecameUnavailable =
      !enabled || !isInitialDataReady;

    previousGateStateRef.current = {
      feedKey,
      enabled,
      isInitialDataReady,
    };

    if (
      feedChanged ||
      becameEnabled ||
      initialDataBecameReady ||
      gateBecameUnavailable
    ) {
      setReadiness(createReadinessState(feedKey));
    }
  }, [
    enabled,
    feedKey,
    isInitialDataReady,
  ]);

  /*
  |---------------------------------------------------------------------------
  | A confirmed empty result needs no Virtuoso measurement
  |---------------------------------------------------------------------------
  */
  useEffect(() => {
    if (!enabled || !isInitialDataReady) return;
    if (items.length !== 0) return;

    updateCurrentFeedReadiness({
      isRenderedRangeReady: true,
    });
  }, [
    enabled,
    isInitialDataReady,
    items.length,
    updateCurrentFeedReadiness,
  ]);

  const handleRangeChanged = useCallback(
    (range) => {
      if (!enabled || !isInitialDataReady) return;
      if (activeFeedKeyRef.current !== feedKey) return;
      if (requiredRenderedEndIndex < 0) return;

      if (scrollParent && scrollParent.clientHeight <= 0) {
        return;
      }

      if ((range?.endIndex ?? -1) >= requiredRenderedEndIndex) {
        updateCurrentFeedReadiness({
          isRenderedRangeReady: true,
        });
      }
    },
    [
      enabled,
      feedKey,
      isInitialDataReady,
      requiredRenderedEndIndex,
      scrollParent,
      updateCurrentFeedReadiness,
    ]
  );

  /*
  |---------------------------------------------------------------------------
  | Remove the skeleton only after the prepared list has painted
  |---------------------------------------------------------------------------
  */
  useEffect(() => {
    if (!enabled || !isInitialDataReady) return undefined;
    if (!currentReadiness.isRenderedRangeReady) return undefined;
    if (currentReadiness.isVisualReady) return undefined;

    return revealAfterStablePaint(() => {
      if (activeFeedKeyRef.current !== feedKey) return;

      updateCurrentFeedReadiness({
        isVisualReady: true,
      });
    });
  }, [
    currentReadiness.isRenderedRangeReady,
    currentReadiness.isVisualReady,
    enabled,
    feedKey,
    isInitialDataReady,
    updateCurrentFeedReadiness,
  ]);

  const previousGateState = previousGateStateRef.current;

  const isActivationTransition =
    previousGateState.feedKey !== feedKey ||
    (enabled && !previousGateState.enabled) ||
    (
      isInitialDataReady &&
      !previousGateState.isInitialDataReady
    );

  const isFeedVisualReady =
    !isActivationTransition &&
    readiness.feedKey === feedKey &&
    readiness.isVisualReady;

  return {
    isVisualReady:
      enabled && isInitialDataReady
        ? isFeedVisualReady
        : false,
    handleRangeChanged,
  };
};

export default useInitialFeedVisualReady;
